/**
 * Utility functions for promo code management
 */

import { getApiUrl } from '../config/api';

export interface PromoCode {
  id: number;
  code: string;
  type: 'percentage' | 'fixed' | 'free_shipping';
  value: number;
  min_amount?: number;
  max_discount?: number;
  usage_limit: number;
  used_count: number;
  valid_from: string;
  valid_until: string;
  is_active: boolean;
  applicable_products?: number[]; // Product IDs
  applicable_categories?: string[]; // Category names
  user_limit?: number; // How many times a user can use this code
}

export interface PromoCodeValidation {
  isValid: boolean;
  message: string;
  discount?: number;
  promoCode?: PromoCode;
}

// Mock promo codes for testing
const MOCK_PROMO_CODES: PromoCode[] = [
  {
    id: 1,
    code: 'WELCOME10',
    type: 'percentage',
    value: 10,
    min_amount: 100,
    max_discount: 50,
    usage_limit: 1000,
    used_count: 150,
    valid_from: '2024-01-01T00:00:00Z',
    valid_until: '2025-12-31T23:59:59Z',
    is_active: true
  },
  {
    id: 2,
    code: 'FREESHIP',
    type: 'free_shipping',
    value: 0,
    min_amount: 0,
    usage_limit: 500,
    used_count: 75,
    valid_from: '2024-01-01T00:00:00Z',
    valid_until: '2025-12-31T23:59:59Z',
    is_active: true
  },
  {
    id: 3,
    code: 'SAVE50',
    type: 'fixed',
    value: 50,
    min_amount: 200,
    usage_limit: 100,
    used_count: 25,
    valid_from: '2024-01-01T00:00:00Z',
    valid_until: '2024-12-31T23:59:59Z',
    is_active: true
  },
  {
    id: 4,
    code: 'FLASH20',
    type: 'percentage',
    value: 20,
    min_amount: 50,
    max_discount: 100,
    usage_limit: 200,
    used_count: 50,
    valid_from: '2024-01-01T00:00:00Z',
    valid_until: '2024-12-31T23:59:59Z',
    is_active: true
  }
];

/**
 * Validates a promo code
 * @param code - The promo code to validate
 * @param cartTotal - Current cart total
 * @param userId - User ID (optional)
 * @param productIds - Array of product IDs in cart
 * @param categories - Array of categories in cart
 * @returns Validation result
 */
export const validatePromoCode = async (
  code: string,
  cartTotal: number,
  userId?: number,
  productIds: number[] = [],
  categories: string[] = []
): Promise<PromoCodeValidation> => {
  try {
    // First try the real API - using configuration
    const response = await fetch(getApiUrl('PROMO_VALIDATE'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: code.toUpperCase(),
        cartTotal,
        userId,
        productIds,
        categories
      })
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    }

    // If API fails, use mock validation for testing
    console.log('API not available, using mock validation');
    return validatePromoCodeMock(code, cartTotal, userId, productIds, categories);
  } catch (error) {
    console.error('Error validating promo code:', error);
    // Use mock validation as fallback
    return validatePromoCodeMock(code, cartTotal, userId, productIds, categories);
  }
};

/**
 * Mock validation function for testing
 */
const validatePromoCodeMock = (
  code: string,
  cartTotal: number,
  userId?: number,
  productIds: number[] = [],
  categories: string[] = []
): PromoCodeValidation => {
  const promoCode = MOCK_PROMO_CODES.find(pc => pc.code === code.toUpperCase());
  
  if (!promoCode) {
    return {
      isValid: false,
      message: 'Code promo invalide ou expiré.'
    };
  }

  // Check if code is active
  if (!promoCode.is_active) {
    return {
      isValid: false,
      message: 'Ce code promo n\'est plus actif.'
    };
  }

  // Check validity dates
  const now = new Date();
  const validFrom = new Date(promoCode.valid_from);
  const validUntil = new Date(promoCode.valid_until);

  if (now < validFrom) {
    return {
      isValid: false,
      message: 'Ce code promo n\'est pas encore valide.'
    };
  }

  if (now > validUntil) {
    return {
      isValid: false,
      message: 'Ce code promo a expiré.'
    };
  }

  // Check usage limit
  if (promoCode.used_count >= promoCode.usage_limit) {
    return {
      isValid: false,
      message: 'Ce code promo a atteint sa limite d\'utilisation.'
    };
  }

  // Check minimum amount
  if (promoCode.min_amount && cartTotal < promoCode.min_amount) {
    return {
      isValid: false,
      message: `Minimum d'achat requis: ${promoCode.min_amount} MAD.`
    };
  }

  // Calculate discount
  let discount = 0;
  switch (promoCode.type) {
    case 'percentage':
      discount = (cartTotal * promoCode.value) / 100;
      if (promoCode.max_discount) {
        discount = Math.min(discount, promoCode.max_discount);
      }
      break;
    case 'fixed':
      discount = promoCode.value;
      break;
    case 'free_shipping':
      discount = 0; // Will be handled in shipping calculation
      break;
  }

  return {
    isValid: true,
    message: `Code promo appliqué ! ${formatPromoCode(promoCode)}`,
    discount,
    promoCode
  };
};

/**
 * Calculates discount amount based on promo code type
 * @param promoCode - The promo code object
 * @param cartTotal - Current cart total
 * @returns Discount amount
 */
export const calculateDiscount = (promoCode: PromoCode, cartTotal: number): number => {
  let discount = 0;

  switch (promoCode.type) {
    case 'percentage':
      discount = (cartTotal * promoCode.value) / 100;
      if (promoCode.max_discount) {
        discount = Math.min(discount, promoCode.max_discount);
      }
      break;
    
    case 'fixed':
      discount = promoCode.value;
      break;
    
    case 'free_shipping':
      // This will be handled separately in shipping calculation
      discount = 0;
      break;
  }

  return Math.max(0, discount);
};

/**
 * Applies promo code to cart total
 * @param promoCode - The promo code object
 * @param cartTotal - Current cart total
 * @param shippingCost - Shipping cost (for free shipping calculation)
 * @returns Object with new totals
 */
export const applyPromoCode = (
  promoCode: PromoCode,
  cartTotal: number,
  shippingCost: number = 0
): {
  subtotal: number;
  discount: number;
  shipping: number;
  total: number;
} => {
  let discount = 0;
  let shipping = shippingCost;

  if (promoCode.type === 'free_shipping') {
    shipping = 0;
    discount = shippingCost; // The discount is the shipping cost
  } else {
    discount = calculateDiscount(promoCode, cartTotal);
  }

  const total = cartTotal - discount + shipping;

  return {
    subtotal: cartTotal,
    discount,
    shipping,
    total: Math.max(0, total)
  };
};

/**
 * Formats promo code for display
 * @param promoCode - The promo code object
 * @returns Formatted string
 */
export const formatPromoCode = (promoCode: PromoCode): string => {
  switch (promoCode.type) {
    case 'percentage':
      return `${promoCode.value}% de réduction`;
    case 'fixed':
      return `${promoCode.value} MAD de réduction`;
    case 'free_shipping':
      return 'Livraison gratuite';
    default:
      return 'Réduction';
  }
};

/**
 * Checks if promo code is expired
 * @param promoCode - The promo code object
 * @returns True if expired
 */
export const isPromoCodeExpired = (promoCode: PromoCode): boolean => {
  const now = new Date();
  const validUntil = new Date(promoCode.valid_until);
  return now > validUntil;
};

/**
 * Checks if promo code is active
 * @param promoCode - The promo code object
 * @returns True if active
 */
export const isPromoCodeActive = (promoCode: PromoCode): boolean => {
  const now = new Date();
  const validFrom = new Date(promoCode.valid_from);
  const validUntil = new Date(promoCode.valid_until);
  
  return promoCode.is_active && 
         now >= validFrom && 
         now <= validUntil && 
         promoCode.used_count < promoCode.usage_limit;
}; 